import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

import { App } from 'antd';

import dayjs from 'dayjs';
import { UnknownContextError } from 'lib/errors/UnkownContextError';
import { handleServiceError } from 'lib/helpers/ServiceHelper';
import { listGuestsLogs } from 'services/Guest.service';
import { listReserves } from 'services/Reserve.service';
import { Guest } from 'types/Guest.type';

export type LoadedGuestLog =
    & Guest.Log.With<'guest'>
    & Guest.Log.With<'vehicle'>
    & Guest.Log.NestedWith<'apartment', ['tower']>;

type Value = {
    isLoading: boolean,
    guestsLogs: LoadedGuestLog[],
    reserves: Reserve.Model[],
    fetchGuestsLogs: () => Promise<void>,
};

type Props = { children: ReactNode };

const GuestsLogsContext = createContext<Value | null>(null);

export function GuestsLogsContextProvider({ children }: Props) {
    const [isLoading, setIsLoading] = useState<Value['isLoading']>(true);
    const [guestsLogs, setGuestsLogs] = useState<Value['guestsLogs']>([]);
    const [reserves, setReserves] = useState<Value['reserves']>([]);

    const app = App.useApp();

    const fetchReserves = useCallback(async () => {
        setIsLoading(true);

        const response = await listReserves();

        setIsLoading(false);

        if (!response.success)
            return handleServiceError(app, response);

        const today = dayjs();
        const yesterday = today.subtract(1, 'day');
        const tomorrow = today.add(1, 'day');

        setReserves(response.reserves.filter(reserve =>
            dayjs(reserve.bookingDate).isBetween(yesterday, tomorrow, 'day', '[]')
        ));
    }, [app]);

    const fetchGuestsLogs: Value['fetchGuestsLogs'] = useCallback(async () => {
        setIsLoading(true);

        const response = await listGuestsLogs();

        setIsLoading(false);

        if (!response.success)
            return handleServiceError(app, response);

        setGuestsLogs(response.guests_logs);
    }, [app]);

    useEffect(() => {
        fetchGuestsLogs();
        fetchReserves();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchGuestsLogs]);

    const value: Value = useMemo(() => ({
        isLoading,
        guestsLogs,
        reserves,
        fetchGuestsLogs,
    }), [isLoading, guestsLogs, reserves, fetchGuestsLogs]);

    return (
        <GuestsLogsContext.Provider value={value}>
            {children}
        </GuestsLogsContext.Provider>
    );
}

export function useGuestsLogs() {
    const context = useContext(GuestsLogsContext);

    if (!context)
        throw new UnknownContextError('GuestsLogsContext');

    return context;
}